import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import {
  Save, X, Camera, Upload, Plus, Trash2,
  GraduationCap, Briefcase, User, Building2,
  MapPin, Phone, ChevronLeft, Eye,
  ArrowLeft, EyeOff, 
} from "lucide-react";
import { Country, State, City } from "country-state-city";
import SelectDropdown from "../../customStyles/SelectDropdown";
import CustomDatePicker from "../../customStyles/CustomDatePicker";
import CroppedImage from "../../helpers/cropImage";
import PageLoader from "../../helpers/PageLoader";
import userImage from "../../assets/images/userImage.png";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
// cspell:words CGPA desg depts desig
// ─────────────────────── static option lists ───────────────────────
const GENDER_OPTIONS = [
  { id: "male", label: "Male" },
  { id: "female", label: "Female" },
  { id: "other", label: "Other" },
  { id: "prefer_not_to_say", label: "Prefer not to say" },
];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => ({ id: b, label: b }));
const MARITAL_STATUS = [
  { id: "single", label: "Single" },
  { id: "married", label: "Married" },
  { id: "divorced", label: "Divorced" },
  { id: "widowed", label: "Widowed" },
];
const EMP_TYPES = [
  { id: "full_time", label: "Full Time" },
  { id: "part_time", label: "Part Time" },
  { id: "contract", label: "Contract" },
  { id: "intern", label: "Intern" },
  { id: "consultant", label: "Consultant" },
];
const EMP_STATUS = [
  { id: "active", label: "Active" },
  { id: "on_leave", label: "On Leave" },
  { id: "suspended", label: "Suspended" },
  { id: "resigned", label: "Resigned" },
  { id: "terminated", label: "Terminated" },
];
const WORK_MODES = [
  { id: "onsite", label: "Onsite" },
  { id: "remote", label: "Remote" },
  { id: "hybrid", label: "Hybrid" },
];

const emptyEducation = { degree: "", institution: "", fieldOfStudy: "", startYear: "", endYear: "", grade: "" };
const emptyWorkExp = { companyName: "", jobTitle: "", startDate: null, endDate: null, isCurrent: false, description: "" };
const emptyEmergency = { name: "", relationship: "", mobile: "" };

const blank = () => ({
  // identity
  firstName: "", lastName: "", email: "", mobile: "", password: "",
  gender: "", dob: null, bloodGroup: "", maritalStatus: "",
  // role/org
  customRoleId: "", departmentId: "", divisionId: "", designationId: "", shiftId: "",
  // employment
  employmentType: "full_time", employmentStatus: "active",
  workLocation: "", workMode: "onsite", payrollEligible: true,
  attendanceDeviceId: "", doj: null, probationEndDate: null,
  // reporting
  reportingManagerId: "", secondaryReportingManagerIds: [],
  // background
  education: [], workExperience: [],
  // contact
  address: { country: "", state: "", city: "", street: "", postalCode: "" },
  emergencyContact: { ...emptyEmergency },
  isActive: true,
});


  // ── Section wrappers ────────────────────────────────────────────
  const Section = ({ icon: Icon, title, children }) => (
    <div className="bg-white rounded-lg p-5 space-y-[10px]">
      <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
        <div className="bg-red-50 p-2 rounded-lg"><Icon className="w-4 h-4 text-red-600" /></div>
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      {children}
    </div>
  );


  const Field = ({ label, required, error, children }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );



// ════════════════════════════════════════════════════════════════════
const CreateEditEmployeeForm = () => {
  const { portalName, employeeId } = useParams(); // employeeId = undefined for create
  const navigate = useNavigate();
  const isEdit = !!employeeId;

  // form
  const [formData, setFormData] = useState(blank());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "preview"
  const [loading, setLoading] = useState(isEdit);

  const [showPassword, setShowPassword] = useState(false);
  const [showPreviewPassword, setShowPreviewPassword] = useState(false);

  // dropdowns
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]); // for reporting manager dropdown
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // profile picture
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // ── load dropdown data ──────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [rolesRes, deptsRes, divsRes, desgRes, empRes, shiftsRes] = await Promise.all([
          api.get(SummaryApi.getAllRoles.url),
          api.get(SummaryApi.getAllDepartments.url),
          api.get(SummaryApi.getAllDivisions.url),
          api.get(SummaryApi.getAllDesignations?.url || "/api/designations"),
          api.get(SummaryApi.getAllUsers.url),
          api.get(SummaryApi.getAllShifts.url),
        ]);

        const rolesData = rolesRes.data.data || [];
        setRoles(rolesData);
        setDepartments(deptsRes.data.data || []);
        setDivisions(divsRes.data.data || []);
        setDesignations(desgRes.data.data || []);
        setShifts(shiftsRes.data.data || []);
        const employeeOptions = (empRes.data.data || []).map((emp) => ({
          ...emp,
          label: `${emp.userId} - ${emp.firstName} ${emp.lastName}`,
        }));

        setEmployees(employeeOptions);

        if (!isEdit) {
          const def = rolesData.find((r) => r.isDefault);
          if (def) setFormData((p) => ({ ...p, customRoleId: def._id }));
        }
      } catch {
        toast.error("Failed to load form data");
      }
    };
    load();
    setCountries(Country.getAllCountries().map((c) => ({ isoCode: c.isoCode, name: c.name })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── load existing employee for edit ────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const res = await api.get(SummaryApi.getOneEmployee?.url(employeeId) || `/api/employees/${employeeId}`);
        const e = res.data.data;
        setFormData({
          firstName: e.firstName || "", lastName: e.lastName || "",
          email: e.email || "", mobile: e.mobile || "", password: "",
          gender: e.gender || "", dob: e.dob || null,
          bloodGroup: e.bloodGroup || "", maritalStatus: e.maritalStatus || "",
          customRoleId: e.customRoleId?._id || e.customRoleId || "",
          departmentId: e.departmentId?._id || e.departmentId || "",
          divisionId: e.divisionId?._id || e.divisionId || "",
          designationId: e.designationId?._id || e.designationId || "",
          shiftId: e.shiftId?._id || e.shiftId || "",
          employmentType: e.employmentType || "full_time",
          employmentStatus: e.employmentStatus || "active",
          workLocation: e.workLocation || "", workMode: e.workMode || "onsite",
          payrollEligible: e.payrollEligible !== false,
          attendanceDeviceId: e.attendanceDeviceId || "",
          doj: e.doj || null, probationEndDate: e.probationEndDate || null,
          reportingManagerId: e.reportingManagerId?._id || e.reportingManagerId || "",
          secondaryReportingManagerIds: (e.secondaryReportingManagerIds || []).map((m) => m._id || m),
          education: (e.education || []).map((ed) => ({ ...ed, startYear: ed.startYear ?? "", endYear: ed.endYear ?? "" })),
          workExperience: e.workExperience || [],
          address: e.address || { country: "", state: "", city: "", street: "", postalCode: "" },
          emergencyContact: e.emergencyContact || { ...emptyEmergency },
          isActive: e.isActive !== false,
        });
        setProfilePreview(e.profilePicture || null);

        // restore state/city dropdowns
        if (e.address?.country) {
          setStates(State.getStatesOfCountry(e.address.country).map((s) => ({ isoCode: s.isoCode, name: s.name })));
        }
        if (e.address?.country && e.address?.state) {
          setCities(City.getCitiesOfState(e.address.country, e.address.state).map((c) => ({ name: c.name })));
        }
      } catch {
        toast.error("Failed to load employee data");
        navigate(`/${portalName}/hr/employee-management`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── field helpers ───────────────────────────────────────────────
  const set = (key, val) => setFormData((p) => ({ ...p, [key]: val }));

  const handleCountryChange = (code) => {
    setFormData((p) => ({ ...p, address: { ...p.address, country: code, state: "", city: "" } }));
    setStates(code ? State.getStatesOfCountry(code).map((s) => ({ isoCode: s.isoCode, name: s.name })) : []);
    setCities([]);
  };
  const handleStateChange = (code) => {
    setFormData((p) => ({ ...p, address: { ...p.address, state: code, city: "" } }));
    setCities(formData.address.country && code
      ? City.getCitiesOfState(formData.address.country, code).map((c) => ({ name: c.name }))
      : []);
  };

  // education
  const addEdu = () => set("education", [...formData.education, { ...emptyEducation }]);
  const removeEdu = (i) => set("education", formData.education.filter((_, idx) => idx !== i));
  const setEdu = (i, field, val) => {
    const arr = [...formData.education];
    arr[i] = { ...arr[i], [field]: val };
    set("education", arr);
  };

  // work experience
  const addExp = () => set("workExperience", [...formData.workExperience, { ...emptyWorkExp }]);
  const removeExp = (i) => set("workExperience", formData.workExperience.filter((_, idx) => idx !== i));
  const setExp = (i, field, val) => {
    const arr = [...formData.workExperience];
    arr[i] = { ...arr[i], [field]: val };
    set("workExperience", arr);
  };

  // ── image handling ──────────────────────────────────────────────
  const compressImage = async (file) => {
    if (file.size <= 2 * 1024 * 1024) return file;
    const compressed = await imageCompression(file, { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true });
    return new File([compressed], file.name, { type: compressed.type });
  };

  const handleFileSelection = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.match("image.*")) { toast.error("Select an image file"); return; }
    const t = toast.loading("Processing image...");
    try {
      const compressed = await compressImage(file);
      const reader = new FileReader();
      reader.onloadend = () => { setImageSrc(reader.result); setShowCropper(true); toast.dismiss(t); };
      reader.readAsDataURL(compressed);
    } catch { toast.update(t, { render: "Failed", type: "error", isLoading: false, autoClose: 3000 }); }
  };

  const cropImageNow = async () => {
    try {
      const { file, fileUrl } = await CroppedImage(imageSrc, croppedAreaPixels);
      const final = await compressImage(file);
      setProfilePictureFile(final);
      setProfilePreview(fileUrl);
      setShowCropper(false);
      setImageSrc(null);
      toast.success(`Picture added (${(final.size / 1024 / 1024).toFixed(1)}MB)`);
    } catch { toast.error("Error cropping image"); }
  };

  // ── validation ──────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = "Required";
    if (!formData.lastName.trim()) errs.lastName = "Required";
    if (!formData.email.trim()) errs.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = "Invalid email";
    if (formData.mobile && !/^[0-9]{10}$/.test(formData.mobile)) errs.mobile = "Must be 10 digits";
    if (!isEdit && !formData.password) errs.password = "Required";
    else if (!isEdit && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}/.test(formData.password)) {
      errs.password = "Min 6 chars with uppercase, lowercase and number";
    }
    if (!formData.customRoleId) errs.customRoleId = "Required";
    if (!formData.doj) errs.doj = "Required";

    formData.education.forEach((ed, i) => {
      if (!ed.degree?.trim()) errs[`edu_${i}_degree`] = "Required";
      if (!ed.institution?.trim()) errs[`edu_${i}_institution`] = "Required";
    });
    formData.workExperience.forEach((ex, i) => {
      if (!ex.companyName?.trim()) errs[`exp_${i}_company`] = "Required";
      if (!ex.jobTitle?.trim()) errs[`exp_${i}_title`] = "Required";
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── submit ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const fd = new FormData();

      const append = (key, val) => {
        if (val !== undefined && val !== null && val !== "") fd.append(key, val);
      };

      append("firstName", formData.firstName);
      append("lastName", formData.lastName);
      append("email", formData.email);
      append("mobile", formData.mobile);
      append("gender", formData.gender);
      append("bloodGroup", formData.bloodGroup);
      append("maritalStatus", formData.maritalStatus);
      if (formData.dob) append("dob", formData.dob);
      if (!isEdit || formData.password) append("password", formData.password);

      append("customRoleId", formData.customRoleId);
      append("departmentId", formData.departmentId);
      append("divisionId", formData.divisionId);
      append("designationId", formData.designationId);
      append("shiftId", formData.shiftId);
      append("employmentType", formData.employmentType);
      append("employmentStatus", formData.employmentStatus);
      append("workLocation", formData.workLocation);
      append("workMode", formData.workMode);
      fd.append("payrollEligible", formData.payrollEligible);
      append("attendanceDeviceId", formData.attendanceDeviceId);
      if (formData.doj) append("doj", formData.doj);
      if (formData.probationEndDate) append("probationEndDate", formData.probationEndDate);
      append("reportingManagerId", formData.reportingManagerId);

      fd.append("secondaryReportingManagerIds", JSON.stringify(formData.secondaryReportingManagerIds));
      fd.append("isActive", formData.isActive);

      // Convert country/state ISO codes to names before sending
      const countryName = countries.find((c) => c.isoCode === formData.address.country)?.name || formData.address.country;
      const stateName = states.find((s) => s.isoCode === formData.address.state)?.name || formData.address.state;
      fd.append("address", JSON.stringify({ ...formData.address, country: countryName, state: stateName }));

      fd.append("emergencyContact", JSON.stringify(formData.emergencyContact));
      fd.append("education", JSON.stringify(
        formData.education.map((e) => ({
          ...e,
          startYear: e.startYear ? Number(e.startYear) : undefined,
          endYear: e.endYear ? Number(e.endYear) : undefined,
        })).filter((e) => e.degree || e.institution)
      ));
      fd.append("workExperience", JSON.stringify(
        formData.workExperience.filter((e) => e.companyName || e.jobTitle)
      ));

      if (profilePictureFile instanceof File) fd.append("profilePicture", profilePictureFile);

      const url = isEdit
        ? SummaryApi.updateEmployee?.url(employeeId) || `/api/employees/${employeeId}`
        : SummaryApi.createEmployee?.url || "/api/employees";

      const res = isEdit ? await api.put(url, fd) : await api.post(url, fd);
      toast.success(res.data.message || (isEdit ? "Updated successfully" : "Created successfully"));
      navigate(`/${portalName}/hr/employee-management`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToPreview = () => {
    if (!validate()) { toast.error("Please fix the errors below"); return; }
    setStep("preview");
  };

  if (loading) return <PageLoader />;

  const inp = (key, placeholder, type = "text", extraClass = "") =>
    <input
      type={type}
      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors[key] ? "border-red-500" : "border-gray-300"} ${extraClass}`}
      placeholder={placeholder}
      value={formData[key]}
      onChange={(e) => set(key, type === "number" ? Number(e.target.value) : e.target.value)}
    />;

  // ══════════════════ PREVIEW STEP ════════════════════════════════
  if (step === "preview") {
    const labelVal = (label, val) => val ? (
      <div><span className="text-xs text-gray-500">{label}</span><p className="text-sm font-medium">{String(val)}</p></div>
    ) : null;

    const roleName = roles.find((r) => r._id === formData.customRoleId)?.roleName;
    const deptName = departments.find((d) => d._id === formData.departmentId)?.name;
    const divName = divisions.find((d) => d._id === formData.divisionId)?.name;
    const desigTitle = designations.find((d) => d._id === formData.designationId)?.title;
    const shiftName = shifts.find((s) => s._id === formData.shiftId)?.name; // <-- 8. Find shift name

    const managerName = (() => { const m = employees.find((e) => e._id === formData.reportingManagerId); return m ? `${m.firstName} ${m.lastName}` : null; })();
    const countryName = countries.find((c) => c.isoCode === formData.address.country)?.name || formData.address.country;
    const stateName = states.find((s) => s.isoCode === formData.address.state)?.name || formData.address.state;

    return (
      <div className="p-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setStep("form")} className="p-2 hover:bg-gray-100 rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Preview — {isEdit ? "Update" : "Create"} Employee</h1>
            <p className="text-sm text-gray-500">Review the details before submitting</p>
          </div>
        </div>

        <div className="space-y-[10px]">
          {/* Profile picture preview */}
          {profilePreview && (
            <div className="flex justify-center">
              <img src={profilePreview} alt="Profile" className="w-24 h-24 rounded-full object-cover border-4 border-red-100" />
            </div>
          )}

          <div className="bg-white rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-gray-700">Personal Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {labelVal("First Name", formData.firstName)}
              {labelVal("Last Name", formData.lastName)}
              {labelVal("Email", formData.email)}
              {labelVal("Mobile", formData.mobile)}
              {labelVal("Gender", GENDER_OPTIONS.find(g => g.id === formData.gender)?.label)}
              {labelVal("Blood Group", formData.bloodGroup)}
              {labelVal("Marital Status", MARITAL_STATUS.find(m => m.id === formData.maritalStatus)?.label)}
              {labelVal("Date of Birth", formData.dob ? new Date(formData.dob).toLocaleDateString() : null)}
              
              {formData.password && (
                <div>
                  <span className="text-xs text-gray-500 block">Password</span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-medium font-mono">
                      {showPreviewPassword ? formData.password : "••••••••"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowPreviewPassword(!showPreviewPassword)}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {showPreviewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <h3 className="font-semibold mb-3 text-gray-700">Employment Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {labelVal("Role", roleName)}
              {labelVal("Department", deptName)}
              {labelVal("Division", divName)}
              {labelVal("Designation", desigTitle)}
              {labelVal("Shift", shiftName)}
              {labelVal("Employment Type", EMP_TYPES.find(e => e.id === formData.employmentType)?.label)}
              {labelVal("Employment Status", EMP_STATUS.find(e => e.id === formData.employmentStatus)?.label)}
              {labelVal("Work Location", formData.workLocation)}
              {labelVal("Work Mode", WORK_MODES.find(w => w.id === formData.workMode)?.label)}
              {labelVal("Date of Joining", formData.doj ? new Date(formData.doj).toLocaleDateString() : null)}
              {labelVal("Probation End", formData.probationEndDate ? new Date(formData.probationEndDate).toLocaleDateString() : null)}
              {labelVal("Reporting Manager", managerName)}
              {labelVal("Payroll Eligible", formData.payrollEligible ? "Yes" : "No")}
            </div>
          </div>

          {formData.address.street || formData.address.country ? (
            <div className="bg-white rounded-lg p-5">
              <h3 className="font-semibold mb-3 text-gray-700">Address</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {labelVal("Street", formData.address.street)}
                {labelVal("Country", countryName)}
                {labelVal("State", stateName)}
                {labelVal("City", formData.address.city)}
                {labelVal("Postal Code", formData.address.postalCode)}
              </div>
            </div>
          ) : null}

          {(formData.emergencyContact.name || formData.emergencyContact.mobile) ? (
            <div className="bg-white rounded-lg p-5">
              <h3 className="font-semibold mb-3 text-gray-700">Emergency Contact</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {labelVal("Name", formData.emergencyContact.name)}
                {labelVal("Relationship", formData.emergencyContact.relationship)}
                {labelVal("Mobile", formData.emergencyContact.mobile)}
              </div>
            </div>
          ) : null}

          {formData.education.length > 0 && (
            <div className="bg-white rounded-lg p-5">
              <h3 className="font-semibold mb-3 text-gray-700">Education</h3>
              {formData.education.map((ed, i) => (
                <div key={i} className="border-l-2 border-red-200 pl-3 mb-3">
                  <p className="font-medium text-sm">{ed.degree} — {ed.institution}</p>
                  <p className="text-xs text-gray-500">{ed.fieldOfStudy} {ed.startYear && `(${ed.startYear} – ${ed.endYear || "present"})`} {ed.grade && `| ${ed.grade}`}</p>
                </div>
              ))}
            </div>
          )}

          {formData.workExperience.length > 0 && (
            <div className="bg-white rounded-lg p-5">
              <h3 className="font-semibold mb-3 text-gray-700">Work Experience</h3>
              {formData.workExperience.map((ex, i) => (
                <div key={i} className="border-l-2 border-blue-200 pl-3 mb-3">
                  <p className="font-medium text-sm">{ex.jobTitle} at {ex.companyName}</p>
                  <p className="text-xs text-gray-500">{ex.isCurrent ? "Current" : ex.endDate ? new Date(ex.endDate).toLocaleDateString() : ""} {ex.description && `| ${ex.description}`}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={() => setStep("form")} className="flex-1 border rounded-lg py-2.5 hover:bg-gray-50 text-sm font-medium">
            Edit Details
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 disabled:opacity-50 text-white rounded-lg py-2.5 flex items-center justify-center gap-2 text-sm font-medium hover:opacity-90"
          >
            <Save className="w-4 h-4" /> {isSubmitting ? "Saving..." : isEdit ? "Update Employee" : "Create Employee"}
          </button>
        </div>
      </div>
    );
  }

  // ══════════════════ FORM STEP ════════════════════════════════════
  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => navigate(-1)}
          className="p-2 rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? "Edit Employee" : "Add New Employee"}</h1>
          <p className="text-sm text-gray-500">Fill in all details then preview before saving</p>
        </div>
      </div>

      <div className="space-y-[10px]">
        {/* ── Profile Picture ─────────────────────────────── */}
        <div className="bg-white rounded-lg p-5">
          <p className="font-semibold text-gray-700 mb-3">Profile Picture (Optional)</p>
          {profilePreview && (
            <div className="flex justify-center mb-4">
              <div className="relative group">
                <img src={profilePreview !== "null" ? profilePreview : userImage} alt="Preview" className="w-28 h-28 rounded-full object-cover border-4 border-red-100" />
                <button type="button" onClick={() => { setProfilePreview(null); setProfilePictureFile(null); }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
          {showCropper ? (
            <div className="max-w-sm mx-auto">
              <div className="relative w-full h-64 bg-black rounded-lg overflow-hidden">
                <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, p) => setCroppedAreaPixels(p)} />
              </div>
              <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(+e.target.value)} className="w-full mt-3" />
              <div className="flex justify-between mt-3">
                <button onClick={() => { setShowCropper(false); setImageSrc(null); }} className="text-gray-500 text-sm flex items-center gap-1"><X className="w-4 h-4" /> Cancel</button>
                <button onClick={cropImageNow} className="bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm">Crop & Use</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3 justify-center">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleFileSelection} className="hidden" />
                <div className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg text-sm transition-colors"><Upload className="w-4 h-4" /> Upload</div>
              </label>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" capture="user" onChange={handleFileSelection} className="hidden" />
                <div className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"><Camera className="w-4 h-4" /> Camera</div>
              </label>
            </div>
          )}
        </div>

        {/* ── Personal Information ─────────────────────────── */}
        <Section icon={User} title="Personal Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="First Name" required error={errors.firstName}>{inp("firstName", "Enter first name")}</Field>
            <Field label="Last Name" required error={errors.lastName}>{inp("lastName", "Enter last name")}</Field>
            <Field label="Email" required error={errors.email}>{inp("email", "user@example.com", "email")}</Field>
            <Field label="Mobile" error={errors.mobile}>
              <input type="tel" className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors.mobile ? "border-red-500" : "border-gray-300"}`} placeholder="10-digit mobile number" value={formData.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} maxLength={10} />
            </Field>
            <Field label="Password" required={!isEdit} error={errors.password}>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`w-full border rounded-lg pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors.password ? "border-red-500" : "border-gray-300"}`}
                  placeholder={isEdit ? "Leave blank to keep current password" : "Min 6 chars, upper/lower/number"}
                  value={formData.password}
                  onChange={(e) => set("password", e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
            <Field label="Date of Birth">
              <CustomDatePicker value={formData.dob} onChange={(d) => set("dob", d)} placeholder="Select date" maxDate={new Date()} />
            </Field>
            <Field label="Gender">
              <SelectDropdown options={GENDER_OPTIONS} value={formData.gender} onChange={(v) => set("gender", v)} placeholder="Select gender" valueKey="id" labelKey="label" />
            </Field>
            <Field label="Blood Group">
              <SelectDropdown options={BLOOD_GROUPS} value={formData.bloodGroup} onChange={(v) => set("bloodGroup", v)} placeholder="Select blood group" valueKey="id" labelKey="label" />
            </Field>
            <Field label="Marital Status">
              <SelectDropdown options={MARITAL_STATUS} value={formData.maritalStatus} onChange={(v) => set("maritalStatus", v)} placeholder="Select status" valueKey="id" labelKey="label" />
            </Field>
          </div>
        </Section>

        {/* ── Employment Details ───────────────────────────── */}
        <Section icon={Building2} title="Employment Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Role" required error={errors.customRoleId}>
              <SelectDropdown options={roles} value={formData.customRoleId} onChange={(v) => set("customRoleId", v)} placeholder="Select role" valueKey="_id" labelKey="roleName" error={errors.customRoleId} />
            </Field>
            <Field label="Department">
              <SelectDropdown options={departments} value={formData.departmentId} onChange={(v) => set("departmentId", v)} placeholder="Select department" valueKey="_id" labelKey="name" />
            </Field>
            <Field label="Division">
              <SelectDropdown options={divisions} value={formData.divisionId} onChange={(v) => set("divisionId", v)} placeholder="Select division" valueKey="_id" labelKey="name" />
            </Field>
            <Field label="Designation">
              <SelectDropdown options={designations} value={formData.designationId} onChange={(v) => set("designationId", v)} placeholder="Select designation" valueKey="_id" labelKey="title" />
            </Field>
            <Field label="Shift">
              <SelectDropdown options={shifts} value={formData.shiftId} onChange={(v) => set("shiftId", v)} placeholder="Select shift"  valueKey="_id" labelKey="name"/>
            </Field>
            <Field label="Employment Type">
              <SelectDropdown options={EMP_TYPES} value={formData.employmentType} onChange={(v) => set("employmentType", v)} placeholder="Select type" valueKey="id" labelKey="label" />
            </Field>
            <Field label="Employment Status">
              <SelectDropdown options={EMP_STATUS} value={formData.employmentStatus} onChange={(v) => set("employmentStatus", v)} placeholder="Select status" valueKey="id" labelKey="label" />
            </Field>
            <Field label="Work Location">{inp("workLocation", "e.g. Chennai HQ, Remote")}</Field>
            <Field label="Work Mode">
              <SelectDropdown options={WORK_MODES} value={formData.workMode} onChange={(v) => set("workMode", v)} placeholder="Select mode" valueKey="id" labelKey="label" />
            </Field>
            <Field label="Date of Joining" required error={errors.doj}>
              <CustomDatePicker value={formData.doj} onChange={(d) => set("doj", d)} placeholder="Select date" error={errors.doj} />
            </Field>
            <Field label="Probation End Date">
              <CustomDatePicker value={formData.probationEndDate} onChange={(d) => set("probationEndDate", d)} placeholder="Select date" />
            </Field>
            <Field label="Reporting Manager">
              <SelectDropdown
                options={employees.filter((e) => e._id !== (isEdit ? employeeId : undefined))}
                value={formData.reportingManagerId}
                onChange={(v) => set("reportingManagerId", v)}
                placeholder="Select primary manager"
                valueKey="_id"
                labelKey="label"
                searchable
              />
            </Field>
            <Field label="Secondary Reporting Managers">
              <SelectDropdown
                options={employees.filter((e) => e._id !== (isEdit ? employeeId : undefined))}
                value={formData.secondaryReportingManagerIds}
                onChange={(v) => set("secondaryReportingManagerIds", v)}
                placeholder="Select Secondary Reporting Managers"
                valueKey="_id"
                labelKey="label"
                searchable
                multiple
              />
            </Field>
            <Field label="Attendance Device ID">{inp("attendanceDeviceId", "Biometric device ID (optional)")}</Field>
            <div className="md:col-span-2 flex items-center gap-3 mt-1">
              <input type="checkbox" id="payrollEligible" checked={formData.payrollEligible} onChange={(e) => set("payrollEligible", e.target.checked)} className="w-4 h-4 accent-red-600" />
              <label htmlFor="payrollEligible" className="text-sm font-medium text-gray-700">Payroll Eligible</label>
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <input type="checkbox" id="isActive" checked={formData.isActive} onChange={(e) => set("isActive", e.target.checked)} className="w-4 h-4 accent-red-600" />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Active Employee</label>
            </div>
          </div>
        </Section>

        {/* ── Address ──────────────────────────────────────── */}
        <Section icon={MapPin} title="Address Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Street">
                <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="Street address" value={formData.address.street} onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, street: e.target.value } }))} />
              </Field>
            </div>
            <Field label="Country">
              <SelectDropdown options={countries} value={formData.address.country} onChange={handleCountryChange} placeholder="Select country" valueKey="isoCode" labelKey="name" searchable />
            </Field>
            <Field label="State">
              <SelectDropdown options={states} value={formData.address.state} onChange={handleStateChange} placeholder={formData.address.country ? "Select state" : "Select country first"} valueKey="isoCode" labelKey="name" disabled={!formData.address.country} searchable />
            </Field>
            <Field label="City">
              <SelectDropdown options={cities} value={formData.address.city} onChange={(v) => setFormData((p) => ({ ...p, address: { ...p.address, city: v } }))} placeholder={formData.address.state ? "Select city" : "Select state first"} valueKey="name" labelKey="name" disabled={!formData.address.state} searchable />
            </Field>
            <Field label="Postal Code">
              <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="Postal code" value={formData.address.postalCode} onChange={(e) => setFormData((p) => ({ ...p, address: { ...p.address, postalCode: e.target.value } }))} />
            </Field>
          </div>
        </Section>

        {/* ── Emergency Contact ─────────────────────────────── */}
        <Section icon={Phone} title="Emergency Contact">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Name">
              <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="Contact name" value={formData.emergencyContact.name} onChange={(e) => setFormData((p) => ({ ...p, emergencyContact: { ...p.emergencyContact, name: e.target.value } }))} />
            </Field>
            <Field label="Relationship">
              <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="e.g. Spouse, Parent" value={formData.emergencyContact.relationship} onChange={(e) => setFormData((p) => ({ ...p, emergencyContact: { ...p.emergencyContact, relationship: e.target.value } }))} />
            </Field>
            <Field label="Mobile">
              <input type="tel" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="10-digit number" value={formData.emergencyContact.mobile} onChange={(e) => setFormData((p) => ({ ...p, emergencyContact: { ...p.emergencyContact, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) } }))} />
            </Field>
          </div>
        </Section>

        {/* ── Education ─────────────────────────────────────── */}
        <Section icon={GraduationCap} title="Education Details">
          {formData.education.map((ed, i) => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50 relative mb-3">
              <button type="button" onClick={() => removeEdu(i)} className="absolute top-3 right-3 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                <Field label="Degree" required error={errors[`edu_${i}_degree`]}>
                  <input type="text" className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors[`edu_${i}_degree`] ? "border-red-500" : "border-gray-300"}`} placeholder="e.g. B.E. Computer Science" value={ed.degree} onChange={(e) => setEdu(i, "degree", e.target.value)} />
                </Field>
                <Field label="Institution" required error={errors[`edu_${i}_institution`]}>
                  <input type="text" className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors[`edu_${i}_institution`] ? "border-red-500" : "border-gray-300"}`} placeholder="e.g. Anna University" value={ed.institution} onChange={(e) => setEdu(i, "institution", e.target.value)} />
                </Field>
                <Field label="Field of Study">
                  <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="e.g. Computer Science" value={ed.fieldOfStudy} onChange={(e) => setEdu(i, "fieldOfStudy", e.target.value)} />
                </Field>
                <Field label="Grade">
                  <input type="text" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="e.g. 8.2 CGPA / First Class" value={ed.grade} onChange={(e) => setEdu(i, "grade", e.target.value)} />
                </Field>
                <Field label="Start Year">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="2018" value={ed.startYear} onChange={(e) => setEdu(i, "startYear", e.target.value)} />
                </Field>
                <Field label="End Year">
                  <input type="number" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" placeholder="2022" value={ed.endYear} onChange={(e) => setEdu(i, "endYear", e.target.value)} />
                </Field>
              </div>
            </div>
          ))}
          <button type="button" onClick={addEdu} className="flex items-center gap-1.5 text-red-600 hover:text-red-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Add Education
          </button>
        </Section>

        {/* ── Work Experience ───────────────────────────────── */}
        <Section icon={Briefcase} title="Previous Work Experience">
          {formData.workExperience.map((ex, i) => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50 relative mb-3">
              <button type="button" onClick={() => removeExp(i)} className="absolute top-3 right-3 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-6">
                <Field label="Company Name" required error={errors[`exp_${i}_company`]}>
                  <input type="text" className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors[`exp_${i}_company`] ? "border-red-500" : "border-gray-300"}`} placeholder="Company name" value={ex.companyName} onChange={(e) => setExp(i, "companyName", e.target.value)} />
                </Field>
                <Field label="Job Title" required error={errors[`exp_${i}_title`]}>
                  <input type="text" className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 ${errors[`exp_${i}_title`] ? "border-red-500" : "border-gray-300"}`} placeholder="Job title" value={ex.jobTitle} onChange={(e) => setExp(i, "jobTitle", e.target.value)} />
                </Field>
                <Field label="Start Date">
                  <CustomDatePicker value={ex.startDate} onChange={(d) => setExp(i, "startDate", d)} placeholder="Start date" />
                </Field>
                <Field label="End Date">
                  <CustomDatePicker value={ex.endDate} onChange={(d) => setExp(i, "endDate", d)} placeholder="End date" disabled={ex.isCurrent} />
                </Field>
                <div className="flex items-center gap-2 md:col-span-2">
                  <input type="checkbox" checked={ex.isCurrent} onChange={(e) => setExp(i, "isCurrent", e.target.checked)} className="w-4 h-4 accent-red-600" />
                  <label className="text-sm text-gray-600">Currently working here</label>
                </div>
                <div className="md:col-span-2">
                  <Field label="Description">
                    <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" rows={2} placeholder="Brief description of role" value={ex.description} onChange={(e) => setExp(i, "description", e.target.value)} />
                  </Field>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addExp} className="flex items-center gap-1.5 text-red-600 hover:text-red-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Add Experience
          </button>
        </Section>
      </div>

      {/* Footer */}
      <div className="flex gap-3 mt-6 pb-6">
        <button onClick={() => navigate(`/${portalName}/hr/employee-management`)} className="flex-1 border rounded-lg py-2.5 hover:bg-gray-50 text-sm font-medium">
          Cancel
        </button>
        <button onClick={handleGoToPreview} className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg py-2.5 flex items-center justify-center gap-2 text-sm font-medium hover:opacity-90 shadow-md">
          <Eye className="w-4 h-4" /> Preview & {isEdit ? "Update" : "Create"}
        </button>
      </div>
    </div>
  );
};

export default CreateEditEmployeeForm;